// Create / edit form for one categories row (pages/CategoriesPage). Plain
// useState, same shape as BankAccountForm.jsx / UserForm.jsx.
//
// One component for both: pass `initialValues` (an existing row) to edit,
// omit it to create. `parentOptions` is the flat category list minus this
// category and its own descendants (a category can't be its own ancestor) —
// the page computes that and passes it in. "" in the parent <select> means
// top level (parent_id = NULL) — on edit that's sent as an explicit null so
// "move to top level" works, not just "re-parent".

import { useState } from 'react';
import { useLanguage } from '../context/LanguageContext.jsx';
import { Spinner } from 'shared/components/Feedback/index.js';
import styles from './CategoryForm.module.css';

function stateFrom(initialValues) {
  return {
    name: initialValues?.name || '',
    nameAm: initialValues?.name_am || '',
    parentId: initialValues?.parent_id != null ? String(initialValues.parent_id) : '',
    sortOrder: initialValues?.sort_order != null ? String(initialValues.sort_order) : '0',
  };
}

export default function CategoryForm({
  initialValues,
  parentOptions = [],
  onSubmit,
  onCancel,
  isPending,
}) {
  const { t, lang } = useLanguage();
  const isEdit = Boolean(initialValues);
  const [values, setValues] = useState(() => stateFrom(initialValues));
  const [clientErrorId, setClientErrorId] = useState(null);

  const set = (field) => (e) => setValues((prev) => ({ ...prev, [field]: e.target.value }));

  const handleSubmit = (e) => {
    e.preventDefault();
    setClientErrorId(null);
    if (!values.name.trim()) {
      setClientErrorId('categories.form.nameRequired');
      return;
    }

    const sortOrder = Number(values.sortOrder);
    const parentId = values.parentId ? Number(values.parentId) : isEdit ? null : undefined;

    onSubmit({
      name: values.name.trim(),
      nameAm: values.nameAm.trim() || undefined,
      parentId,
      sortOrder: Number.isFinite(sortOrder) ? sortOrder : 0,
    });
  };

  return (
    <form className={styles.form} onSubmit={handleSubmit}>
      <h3 className={styles.title}>
        {isEdit ? t('categories.form.editTitle') : t('categories.form.createTitle')}
      </h3>

      <div className={styles.grid}>
        <label className={styles.field}>
          {t('categories.form.nameLabel')}
          <input
            type="text"
            value={values.name}
            onChange={set('name')}
            autoFocus
            maxLength={100}
            className={styles.input}
          />
        </label>

        <label className={styles.field}>
          {t('categories.form.nameAmLabel')}
          <input
            type="text"
            value={values.nameAm}
            onChange={set('nameAm')}
            maxLength={100}
            className={styles.input}
            lang="am"
          />
        </label>

        <label className={styles.field}>
          {t('categories.form.parentLabel')}
          <select value={values.parentId} onChange={set('parentId')} className={styles.input}>
            <option value="">{t('categories.form.parentNone')}</option>
            {parentOptions.map((c) => (
              <option key={c.id} value={c.id}>
                {lang === 'am' && c.name_am ? c.name_am : c.name}
              </option>
            ))}
          </select>
        </label>

        <label className={styles.field}>
          {t('categories.form.sortOrderLabel')}
          <input
            type="number"
            value={values.sortOrder}
            onChange={set('sortOrder')}
            className={`${styles.input} tabularNums`}
          />
          <span className={styles.hint}>{t('categories.form.sortOrderHint')}</span>
        </label>
      </div>

      {clientErrorId && <p className={styles.error}>{t(clientErrorId)}</p>}

      <div className={styles.actions}>
        <button type="button" className={styles.cancelButton} onClick={onCancel}>
          {t('categories.form.cancel')}
        </button>
        <button type="submit" className={styles.submitButton} disabled={isPending}>
          {isPending && <Spinner />}{' '}
          {isPending
            ? t('categories.form.submitting')
            : isEdit
              ? t('categories.form.submitEdit')
              : t('categories.form.submitCreate')}
        </button>
      </div>
    </form>
  );
}
